import { EventEmitter, Injectable, OnDestroy } from '@angular/core';
import { Feedstock } from '../../../models/project/feedstock/feedstock.model';
import { Product } from '../../../models/project/plant-specifications/product.model';
import {
  PlantSpecifications,
  PlantUnitType
} from '../../../models/project/plant-specifications/plant-specifications.model';
import Utils from '../../../util';
import { InputSummary } from '../../../models/input-summary';
import { Unit, UnitService } from '../../anessa-library/anessa-library/projects/unit-library/src/public-api';
import { ProjectCommunicationService } from '../../../communication/project-comms.service';
import { UnitValue } from '../../../models/data-structures/unit-value.model';
import { AnessaTranslationService } from '../../translation/anessa-translation.service';
import { takeUntil } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class PopupSummaryGeneratorService implements OnDestroy {
  nameHeader?: string;
  valueHeader?: string;
  unitHeader?: string;
  private onDestroyNotifier = new EventEmitter();

 constructor(
    private unitService: UnitService,
    private projectCommService: ProjectCommunicationService,
    private translationService: AnessaTranslationService
  ) {
   this.setHeaderTranslations();
   this.translationService.languageChangeNotifier
     .pipe(takeUntil(this.onDestroyNotifier))
     .subscribe(_language => {
       this.setHeaderTranslations();
     });
  }

  header?: string;

  ngOnDestroy() {
    this.onDestroyNotifier.emit();
    this.onDestroyNotifier.unsubscribe();
  }

  public getPopupInputSummary(
    model: Feedstock | Product | PlantSpecifications,
    id: string
  ): string {
    if (!model) {
      return '';
    }
    switch (model.constructor) {
      case Feedstock:
        if (model instanceof Feedstock) {
          return this.getFeedstockInputSummary(new Feedstock(model)) ?? '';
        }
        break;
      case Product:
        if (model instanceof Product) {
          return this.getSummaryTable(new Product(model).getInputSummary([])) ?? '';
        }
        break;
      case PlantSpecifications:
        if (model instanceof PlantSpecifications) {
          return this.getPlantSpecInputSummary(new PlantSpecifications(model), id) ?? '';
        }
        break;
      default:
        return '';
    }
    return '';
  }

  private getFeedstockInputSummary(model: Feedstock) {
    const summary = model.getInputSummary([]);
    const inputs = summary.inputs;

    let res = this.header;
    for (let i = 0; i < inputs.length; i++) {
      const input = inputs[i];
      res = res + this.getHTMLRow(
        this.processName(input.name),
        input.value,
        input.unit
      );
    }
    const bmpTable = this.getSummaryTable(model.bmpPotential.getInputSummary([]));
    if (bmpTable) {
      res += '<div class="flow-diagram-row"><p>bmp</p></div>';
      res += bmpTable;
    }
    return res;
  }

  private getPlantSpecInputSummary(plantSpec: PlantSpecifications, id: string) {
    let summary: InputSummary;

    switch (id) {
      case PlantUnitType.GENERATOR:
      case PlantUnitType.UPGRADER:
      case PlantUnitType.BOILER:
      case PlantUnitType.CHP:
        summary = plantSpec.getPlantUnitInputs(id, []);
        break;
      case PlantUnitType.DEWATERING:
        summary = plantSpec.getDewateringInputs([]);
        break;
      case 'digester':
        summary = plantSpec.getDigesterInputs([]);
        break;
      default:
        console.log('no plant spec summary found for ' + id);
        return null;
    }
    return this.getSummaryTable(summary);
  }

  getHTMLRowFromUV(name: string, unitValue: UnitValue) {
    return this.getHTMLRow(this.processName(name), unitValue.value, unitValue.unit);
  }

  getHTMLRow(name: string, value: number | string | null, unit: string | null) {
    let unitName = '';
    if (unit) {
      const unitActual = this.unitService.getUnitById(unit);
      if (unitActual) {
        const unitTranslation = this.translationService.get(unitActual.translationId ?? '');
        unitName = Unit.parseUnitDisplayName(unitTranslation);
      }
    }
    if (Utils.isNumeric(value)) {
      value = Utils.formatNumber(Number(value), this.projectCommService.getPrecision());
    } else if (typeof value === 'string') {
      value = this.translationService.get(value);
    }
    const row = '<div class="flow-diagram-row"><div class="flow-diagram-cell flow-diagram-title-cell">' + name + '</div>'
      + '<div class="flow-diagram-cell flow-diagram-value-cell">' + value + '</div>'
      + '<div class="flow-diagram-cell flow-diagram-unit-cell">' + unitName + '</div></div>';
    return row;
    // return '<p>' + this.name + ': ' + this.value + '</p>';
  }

  private getSummaryTable(sum: InputSummary) {
    const inputs = sum.inputs;
    let res = this.header;
    for (let i = 0; i < inputs.length; i++) {
      const input = inputs[i];
      res = res + this.getHTMLRow(this.processName(input.name), this.processName(input.value), input.unit);
    }
    return res;
  }

  private processName(sectionTitle: string) {
    // if the section title includes a period it's prob a translation id
    // if it does and it's not a translation it it will still be displayed normally
    // but there might be a console log, it's fine though :D
    if (sectionTitle.includes('.')) {
      return this.translationService.get(sectionTitle);
    }
    return sectionTitle;
  }

  private setHeaderTranslations() {
    this.nameHeader = this.translationService.get('RESULTS.GENERAL.NAME');
    this.valueHeader = this.translationService.get('RESULTS.GENERAL.VALUE');
    this.unitHeader = this.translationService.get('RESULTS.GENERAL.UNIT');

    this.header = '<div class="flow-diagram-row flow-diagram-header-row"><div class="flow-diagram-cell flow-diagram-title-cell"> ' +
    this.nameHeader + ' </div>'
    + '<div class="flow-diagram-cell flow-diagram-value-cell"> ' + this.valueHeader + ' </div>'
    + '<div class="flow-diagram-cell flow-diagram-unit-cell"> ' + this.unitHeader + ' </div></div>';
  }
}
